import * as React from "react";
import { useTerms } from "@/hooks/useTerms";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { Check, Loader2, Search, UserMinus, UserPlus, Users } from "lucide-react";
import type { StaffRosterItem } from "@eduviaos/shared";
import { useAssignEnquiry, useStaffRoster } from "@/hooks/useAdmissions";

/**
 * AssignmentMenu — Phase D5.
 *
 * Compact dropdown for assigning the primary owner of an enquiry.
 * Powered by `GET /admissions/staff` (active assignable users in the
 * tenant), with built-in:
 *
 *   - Quick search across name + email — works on roster sizes well
 *     beyond a typical school's staff (50-200) without virtualisation.
 *   - Inline "Unassigned" entry that POSTs `assigned_to=null` and
 *     auto-logs an `assigned` activity with metadata.cleared=true.
 *   - Current selection check ✓ + tone for the active row, mirroring
 *     shadcn's combobox-by-dropdown pattern (we deliberately don't
 *     pull in a third dependency for a single-pick picker).
 *
 * Contract:
 *   - `currentUserId` — pre-selected owner; null = unassigned.
 *   - `enquiryId` — drives the mutation.
 *   - `onAssigned` — called with the resolved staff member after a
 *     successful assignment so the parent UI can flash a toast.
 */

interface AssignmentMenuProps {
  enquiryId: string;
  currentUserId: string | null;
  /** Optional label for the trigger button (default = "Assignee"). */
  triggerLabel?: string;
  /** Optional className passthrough so callers can tighten the
   * trigger inside crowded toolbars. */
  triggerClassName?: string;
  onAssigned?: (assignee: StaffRosterItem | null) => void;
}

function displayName(s: StaffRosterItem): string {
  return s.full_name?.trim() || s.email;
}

function initialsOf(s: StaffRosterItem): string {
  const src = s.full_name?.trim() || s.email.split("@")[0]!;
  return (
    src
      .split(/[.\s_-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

export function AssignmentMenu({
  enquiryId,
  currentUserId,
  triggerLabel = "Assignee",
  triggerClassName,
  onAssigned,
}: AssignmentMenuProps) {
  const { ct } = useTerms();
  const { data, isLoading } = useStaffRoster();
  const mut = useAssignEnquiry(enquiryId);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const items = React.useMemo(() => data?.items ?? [], [data?.items]);
  const currentStaff = items.find((s) => s.user_id === currentUserId) ?? null;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => {
      const name = (s.full_name ?? "").toLowerCase();
      const email = s.email.toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [items, query]);

  const onPick = async (next: StaffRosterItem | null) => {
    if ((next?.user_id ?? null) === currentUserId) {
      setOpen(false);
      return;
    }
    try {
      await mut.mutateAsync({ assigned_to: next?.user_id ?? null });
      // Phase E1.b.3 — global toast for assignment success. The menu
      // closes on selection, so the in-place error footer that used
      // to render below the staff list is gone too; failures now
      // route through `toast.error` (the dropdown stays open if the
      // mutation rejects so the IA can retry with a different pick).
      toast.success(
        next ? `Assigned to ${displayName(next)}.` : "Unassigned — no owner on this enquiry.",
      );
      onAssigned?.(next);
      setOpen(false);
    } catch (e) {
      toast.error((e as { message?: string } | null)?.message ?? "Couldn't assign — try again.");
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("justify-start", triggerClassName)}
          aria-label={
            currentStaff
              ? `${triggerLabel}: ${displayName(currentStaff)} — change`
              : `${triggerLabel}: unassigned — pick owner`
          }
          data-testid="assignment-trigger"
        >
          {currentStaff ? (
            <>
              <span
                aria-hidden="true"
                className="bg-primary/15 text-primary inline-flex size-5 items-center justify-center rounded-full text-[10px] font-semibold"
              >
                {initialsOf(currentStaff)}
              </span>
              <span className="max-w-[180px] truncate text-left">{displayName(currentStaff)}</span>
            </>
          ) : (
            <>
              <UserPlus className="size-4" aria-hidden="true" />
              <span className="text-muted-foreground">Unassigned</span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[360px] w-[320px] overflow-hidden p-0">
        <DropdownMenuLabel className="flex items-center gap-2 border-b px-3 py-2">
          <Users className="size-3.5" aria-hidden="true" />
          Assign owner
        </DropdownMenuLabel>
        <div className="border-border/60 border-b p-2">
          <div className="relative">
            <Search
              className="text-muted-foreground absolute left-2 top-1/2 size-3.5 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={ct("Search staff…")}
              className="h-8 pl-7 text-xs"
              aria-label={ct("Search staff")}
              data-testid="assignment-search"
            />
          </div>
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1">
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              void onPick(null);
            }}
            className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-xs"
            data-testid="assignment-unassigned"
          >
            <span className="flex items-center gap-2">
              <UserMinus className="text-muted-foreground size-3.5" aria-hidden="true" />
              Unassigned
            </span>
            {currentUserId === null && (
              <Check className="text-primary size-3.5" aria-hidden="true" />
            )}
          </DropdownMenuItem>
          {isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-xs">
              <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              {ct("Loading staff…")}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-muted-foreground px-3 py-6 text-center text-xs">
              No staff match "{query}".
            </div>
          ) : (
            <>
              <DropdownMenuSeparator className="my-1" />
              {filtered.map((staff) => {
                const active = staff.user_id === currentUserId;
                return (
                  <DropdownMenuItem
                    key={staff.user_id}
                    onSelect={(e) => {
                      e.preventDefault();
                      void onPick(staff);
                    }}
                    className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-xs"
                    data-testid={`assignment-staff-${staff.user_id}`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="bg-muted text-muted-foreground inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                      >
                        {initialsOf(staff)}
                      </span>
                      <span className="min-w-0 truncate">
                        <span className="text-foreground block truncate">{displayName(staff)}</span>
                        {staff.full_name && (
                          <span className="text-muted-foreground block truncate text-[10px]">
                            {staff.email}
                          </span>
                        )}
                      </span>
                    </span>
                    {active && (
                      <Check className="text-primary size-3.5 shrink-0" aria-hidden="true" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
